import type { AuthenticationInformation } from '../../domain'

import type { AuthenticationPersistenceService } from '../../application'

import { BaseWebStorage } from './Base'
import { isSerializedAuthenticationInformation } from './Validators'

/**
 * This localStorage field is shared across the application
 * Do **not** change its value
 */
const SHARED_TOKEN_FIELD = 'token'

export type SerializedAuthenticationInformation = [
    AuthenticationInformation['token'],
    AuthenticationInformation['username'],
    /** @see {AuthenticationInformation['expiresAt']['getTime']} */
    number,
    AuthenticationInformation['capabilities'],
]

export class WebStorageAuthenticationPersistenceService extends BaseWebStorage<AuthenticationInformation | null, SerializedAuthenticationInformation>
    implements AuthenticationPersistenceService
{
    protected readonly fallbackValue = null

    protected readonly key = 'authKey'

    private readonly rawStorage: Pick<Storage, 'removeItem' | 'setItem'>

    constructor (...[storage, version]: ConstructorParameters<typeof BaseWebStorage<AuthenticationInformation | null, SerializedAuthenticationInformation>>) {
        super(storage, version)
        this.rawStorage = storage
    }

    protected toSerializable ({ token, username, expiresAt, capabilities }: AuthenticationInformation): SerializedAuthenticationInformation {
        return [token, username, expiresAt.getTime(), capabilities]
    }

    protected isValidSerializable (unknownValue: unknown): unknownValue is SerializedAuthenticationInformation {
        return isSerializedAuthenticationInformation(unknownValue)
    }

    protected fromSerializable ([token, username, expiresAt, capabilities]: SerializedAuthenticationInformation): AuthenticationInformation {
        return { token, username, expiresAt: new Date(expiresAt), capabilities, obsolete: null, expiresSoon: false }
    }

    retrieve (): AuthenticationInformation | null {
        return this.retrieveDeserialize()
    }

    flush (): void {
        this.remove()
        this.rawStorage.removeItem(SHARED_TOKEN_FIELD)
    }

    persist (info: AuthenticationInformation): void {
        this.persistSerialize(info)
        this.rawStorage.setItem(SHARED_TOKEN_FIELD, info.token)
    }
}
